import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { REFINED } from "../risikoanalyseStep/tokens";
import { alleDokumente } from "../windowManager/browserFenster/fensterRegister";

/**
 * „Um Genehmigung bitten" — kleiner kvp-rd-Dialog der Kontakt-Detailansicht.
 * Fragt eine optionale Begründung ab und legt eine Freigabe-Anfrage an
 * (Genehmigungen, 30.09.2026; gestuft seit 01.10.2026): FO/BO des Kontakts
 * und die Administration werden gefragt, bleibt die Anfrage offen, die
 * Geschäftsführung. Erst die Genehmigung schaltet das Bearbeiten frei.
 *
 * `KeineRechteDialog` baut darauf auf: Er setzt Einleitung (wer zuständig
 * ist) und Status (Anfrage läuft/abgelehnt) über die optionalen Props.
 */
export interface ApprovalRequestDialogProps {
    open: boolean;
    contactName: string;
    /** Läuft gerade ein Versand? Sperrt die Bedienelemente. */
    busy?: boolean;
    /** Fehlermeldung des letzten Versuchs (bleibt im Dialog stehen). */
    error?: string | null;
    onSubmit: (begruendung: string) => void;
    onCancel: () => void;
    /** Überschrift und aria-Label des Dialogs. */
    titel?: string;
    /** Ersetzt den Standard-Einleitungstext. */
    einleitung?: React.ReactNode;
    /** Stand einer bestehenden Anfrage — steht über dem Begründungsfeld. */
    statusText?: React.ReactNode;
    /** `false` blendet Begründung und Senden aus (z. B. Anfrage läuft schon). */
    sendenMoeglich?: boolean;
    /**
     * Senden ist möglich, aber nicht der Standard (nach einer Ablehnung): Das
     * Formular erscheint erst nach „Erneut anfragen".
     */
    sendenZugeklappt?: boolean;
    /** Farbe des Status-Kastens — „abgelehnt" hebt ihn als Fehlerton hervor. */
    statusTon?: "neutral" | "abgelehnt";
}

const REASON_MAX = 1000;

/** Stand einer Freigabe-Anfrage (Server: `ContactApprovalGrantInfo`). */
export interface ApprovalAnfrage {
    id: number;
    contactId: number;
    status: "PENDING" | "GRANTED" | "DENIED" | "REVOKED";
    reason: string | null;
    createdAt: string;
    decidedAt: string | null;
    validUntil: string | null;
    abgelaufen: boolean;
    /** Wer entschieden hat (Anzeigename) — vom Server, sofern bekannt. */
    decidedByName?: string | null;
}

/** Antwort von `POST /api/contact/:id/approval-request`. */
export interface ApprovalRequestResponse {
    status: "sent" | "pending" | "already_writable" | "throttled" | "no_recipients";
    recipients: string[];
    anfrage?: ApprovalAnfrage | null;
}

/** Zustand des Dialogs „Um Genehmigung bitten". */
export interface ApprovalUiState {
    open: boolean;
    busy: boolean;
    /** Fehlermeldung des letzten Versuchs (im Dialog). */
    error: string | null;
}

export const APPROVAL_INITIAL: ApprovalUiState = {
    open: false,
    busy: false,
    error: null,
};

/** Übersetzt die Server-Antwort in den Satz der Rückmeldung. */
export const approvalMeldung = (r: ApprovalRequestResponse): string => {
    const namen = (r.recipients ?? []).filter(Boolean).join(", ");
    switch (r.status) {
        case "sent":
            return namen
                ? `Anfrage an ${namen} gesendet. Du bekommst eine Mitteilung, sobald entschieden ist.`
                : "Anfrage gesendet. Du bekommst eine Mitteilung, sobald entschieden ist.";
        case "pending":
            return "Deine Anfrage läuft bereits — du bekommst eine Mitteilung, sobald entschieden ist.";
        case "already_writable":
            return "Du darfst diesen Kontakt bereits bearbeiten — bitte die Seite neu laden.";
        case "throttled":
            return "Du hast eben erst angefragt — bitte ein paar Minuten warten.";
        default:
            return "Anfrage gespeichert. Es war niemand zu benachrichtigen — bitte an die Administration wenden.";
    }
};

/**
 * Liegt danach eine Anfrage beim Server? Dann ist der Dialog erledigt.
 * `already_writable` hat nichts angelegt — dort bleibt ein erneuter Versuch
 * möglich (die Seite muss ohnehin neu geladen werden).
 */
export const approvalGesendet = (r: ApprovalRequestResponse): boolean =>
    r.status !== "already_writable";

export const ApprovalRequestDialog: React.FC<ApprovalRequestDialogProps> = ({
    open,
    contactName,
    busy = false,
    error = null,
    onSubmit,
    onCancel,
    titel = "Um Genehmigung bitten",
    einleitung,
    statusText,
    sendenMoeglich = true,
    sendenZugeklappt = false,
    statusTon = "neutral",
}) => {
    const [begruendung, setBegruendung] = useState("");
    const [aufgeklappt, setAufgeklappt] = useState(false);
    const areaRef = useRef<HTMLTextAreaElement | null>(null);
    const formularSichtbar = sendenMoeglich && (!sendenZugeklappt || aufgeklappt);

    // Beim Öffnen den Text leeren und den Fokus setzen. Absichtlich nur an
    // `open` gebunden: der Text darf beim Tippen nicht von einem busy-Wechsel
    // zurückgesetzt werden.
    useEffect(() => {
        if (!open) return;
        setBegruendung("");
        setAufgeklappt(false);
        const t = window.setTimeout(() => areaRef.current?.focus(), 0);
        return () => window.clearTimeout(t);
    }, [open]);

    // „Erneut anfragen" geklickt → Fokus ins Begründungsfeld.
    useEffect(() => {
        if (!aufgeklappt) return;
        const t = window.setTimeout(() => areaRef.current?.focus(), 0);
        return () => window.clearTimeout(t);
    }, [aufgeklappt]);

    // ESC schließt — aber nicht während des Versands. `busy` und `onCancel`
    // gehören in die Dependencies, sonst friert der Handler auf dem Stand beim
    // Öffnen ein und ESC bricht mitten im Senden ab (wie in ConfirmDialog).
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape" && !busy) onCancel();
        };
        alleDokumente.addEventListener("keydown", onKey);
        return () => alleDokumente.removeEventListener("keydown", onKey);
    }, [open, busy, onCancel]);

    if (!open) return null;

    return createPortal(
        <div
            role="presentation"
            onMouseDown={() => {
                if (!busy) onCancel();
            }}
            style={{
                position: "fixed",
                inset: 0,
                zIndex: 100002,
                background: "rgba(20,8,20,0.34)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 24,
                fontFamily: REFINED.fontUI,
            }}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-label={titel}
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                    width: "min(480px, 100%)",
                    background: REFINED.white,
                    border: `1px solid ${REFINED.line}`,
                    borderRadius: 14,
                    boxShadow: REFINED.cardShadow,
                    overflow: "hidden",
                    color: REFINED.ink,
                }}
            >
                <div
                    style={{
                        padding: "15px 18px",
                        borderBottom: `2px solid ${REFINED.goldLine}`,
                    }}
                >
                    <h3 style={{ margin: 0, fontSize: REFINED.fs.lg, fontWeight: 800 }}>
                        {titel}
                    </h3>
                </div>

                <div style={{ padding: "16px 18px", fontSize: REFINED.fs.sm, color: REFINED.ink2, lineHeight: 1.5 }}>
                    <div style={{ margin: "0 0 12px" }}>
                        {einleitung ?? (
                            <p style={{ margin: 0 }}>
                                Die Anfrage für <strong style={{ color: REFINED.ink }}>{contactName}</strong>{" "}
                                geht an die Zuständigen (FO/BO) und die Administration; bleibt sie
                                offen, wird die Geschäftsführung gefragt. Wird sie genehmigt, darfst
                                du den Kontakt bearbeiten.
                            </p>
                        )}
                    </div>
                    {statusText && (
                        <p
                            role="status"
                            style={{
                                margin: "0 0 12px",
                                padding: "8px 11px",
                                borderRadius: 10,
                                background: REFINED.fieldBg,
                                border: `1px solid ${statusTon === "abgelehnt" ? REFINED.err : REFINED.line}`,
                                color: statusTon === "abgelehnt" ? REFINED.err : REFINED.ink,
                                fontWeight: 700,
                            }}
                        >
                            {statusText}
                        </p>
                    )}
                    {formularSichtbar && (
                        <>
                            <label
                                htmlFor="approval-reason"
                                style={{
                                    display: "block",
                                    marginBottom: 6,
                                    fontSize: REFINED.fs.xxs,
                                    fontWeight: 800,
                                    letterSpacing: ".04em",
                                    textTransform: "uppercase",
                                    color: REFINED.ink3,
                                }}
                            >
                                Begründung (optional)
                            </label>
                            <textarea
                                id="approval-reason"
                                ref={areaRef}
                                value={begruendung}
                                maxLength={REASON_MAX}
                                disabled={busy}
                                onChange={(e) => setBegruendung(e.target.value)}
                                placeholder="Was soll geändert werden — und warum?"
                                rows={4}
                                style={{
                                    width: "100%",
                                    boxSizing: "border-box",
                                    resize: "vertical",
                                    font: "inherit",
                                    fontSize: REFINED.fs.sm,
                                    color: REFINED.ink,
                                    background: REFINED.fieldBg,
                                    border: `1px solid ${REFINED.line}`,
                                    borderRadius: 10,
                                    padding: "9px 11px",
                                }}
                            />
                        </>
                    )}
                    {error && (
                        <p style={{ margin: "10px 0 0", color: REFINED.err, fontWeight: 700 }}>{error}</p>
                    )}
                </div>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 10,
                        padding: "12px 18px",
                        borderTop: `1px solid ${REFINED.line}`,
                        background: REFINED.fieldBg,
                    }}
                >
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={busy}
                        style={{
                            appearance: "none",
                            font: "inherit",
                            fontSize: REFINED.fs.sm,
                            fontWeight: 700,
                            padding: "8px 16px",
                            borderRadius: 10,
                            cursor: busy ? "not-allowed" : "pointer",
                            background: REFINED.white,
                            border: `1px solid ${REFINED.aub100}`,
                            color: REFINED.aub,
                            opacity: busy ? 0.55 : 1,
                        }}
                    >
                        {formularSichtbar ? "Abbrechen" : "Schließen"}
                    </button>
                    {sendenMoeglich && !formularSichtbar && (
                    <button
                        type="button"
                        onClick={() => setAufgeklappt(true)}
                        disabled={busy}
                        style={{
                            appearance: "none",
                            font: "inherit",
                            fontSize: REFINED.fs.sm,
                            fontWeight: 700,
                            padding: "8px 16px",
                            borderRadius: 10,
                            cursor: busy ? "not-allowed" : "pointer",
                            background: REFINED.white,
                            border: `1px solid ${REFINED.aub}`,
                            color: REFINED.aub,
                        }}
                    >
                        Erneut anfragen
                    </button>
                    )}
                    {formularSichtbar && (
                    <button
                        type="button"
                        onClick={() => onSubmit(begruendung.trim())}
                        disabled={busy}
                        style={{
                            appearance: "none",
                            font: "inherit",
                            fontSize: REFINED.fs.sm,
                            fontWeight: 700,
                            padding: "8px 16px",
                            borderRadius: 10,
                            cursor: busy ? "not-allowed" : "pointer",
                            background: REFINED.aub,
                            border: `1px solid ${REFINED.aub}`,
                            color: REFINED.white,
                            opacity: busy ? 0.6 : 1,
                        }}
                    >
                        {busy ? "Wird gesendet…" : "Um Genehmigung bitten"}
                    </button>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default ApprovalRequestDialog;
